import PropTypes from "prop-types";
import * as Yup from "yup";
import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
// form
import {useForm, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
// @mui
import {LoadingButton} from "@mui/lab";
import {Box, Card, CircularProgress, Grid, InputAdornment, Stack, TextField} from "@mui/material";
import {useSnackbar} from "notistack";
// assets
import {countries} from "../../assets/data";
// components
import FormProvider, {RHFAutocomplete, RHFSelect, RHFTextField} from "../../components/hook-form";
import {PATH_DASHBOARD} from "../../routes/path";
import {dispatch, useSelector} from "../../redux/store";
import {addCustomer, fetchAllCustomers, getClientDetails, updateCustomer} from "../../redux/slices/customers";
import {
    CUSTOMER_TYPES,
    DISABLE_TIN,
    isCustomerType,
    ONLY_TIN,
    OTHER_IDENTIFICATIONS,
    REQUIRED_JBKJS,
    REQUIRED_JMBG
} from "../../constants";

// ----------------------------------------------------------------------

// Šifarnik zemalja je jedini izvor vrednosti za polje „Zemlja" - vrednost van njega ostavlja
// padajuću listu praznom.
const SERBIA = countries.find(country => country.code === "RS")?.label || "Srbija";

// Dužina PIB-a; kraći unos se ne šalje na proveru.
const TIN_LENGTH = 9;

CustomersNewEditForm.propTypes = {
    isEdit: PropTypes.bool,
    currentCustomer: PropTypes.object
};

export default function CustomersNewEditForm({currentCustomer, searched, handleSaveAndSubmit}) {

    // searched znaci da je korisnik trazen prilikom izdavanja b2b

    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const [loadingTin, setLoadingTin] = useState(false);

    const {allCustomers} = useSelector(state => state.customers);

    useEffect(() => {
        if (!currentCustomer && allCustomers.length === 0) {
            dispatch(fetchAllCustomers()).unwrap().catch(e => console.error("fetchAllCustomers", e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Obavezna polja zavise od tipa kupca: pravno lice ima PIB, fizičko lice broj lične karte ili
    // pasoša. Bezuslovno traženje PIB-a je onemogućavalo unos svih tipova osim 10, 12 i 40.
    const NewUserSchema = Yup.object().shape({
        taxCorePrefix: Yup.number().typeError("Tip kupca je obavezan").required("Tip kupca je obavezan"),
        name: Yup.string().required("Naziv je obavezan"),
        tin: Yup.string().when("taxCorePrefix", {
            is: value => isCustomerType(ONLY_TIN, value),
            then: () => Yup.string().required("PIB je obavezan"),
            otherwise: () => Yup.string().nullable()
        }),
        jmbg: Yup.string().when("taxCorePrefix", {
            is: value => isCustomerType(REQUIRED_JMBG, value),
            then: () => Yup.string()
                .matches(/^[0-9]+$/, "Unesite samo brojeve")
                .required("Matični broj je obavezan"),
            otherwise: () => Yup.string().nullable()
        }),
        jbkjs: Yup.string().when("taxCorePrefix", {
            is: value => isCustomerType(REQUIRED_JBKJS, value),
            then: () => Yup.string().required("JBKJS je obavezan"),
            otherwise: () => Yup.string().nullable()
        }),
        otherIndificationNumber: Yup.string().when("taxCorePrefix", {
            is: value => isCustomerType(OTHER_IDENTIFICATIONS, value),
            then: () => Yup.string().required("Identifikacioni broj je obavezan"),
            otherwise: () => Yup.string().nullable()
        }),
        address: Yup.string().nullable(),
        city: Yup.string().nullable(),
        country: Yup.string().nullable()
    });

    const defaultValues = useMemo(
        () => ({
            name: currentCustomer?.name || searched?.name || "",
            // Kupac nađen po PIB-u je pravno lice - biletar ne bira tip svaki put ručno.
            taxCorePrefix: currentCustomer?.taxCorePrefix || (searched?.TIN ? 10 : null),
            tin: currentCustomer?.tin || searched?.TIN || "",
            jmbg: currentCustomer?.jmbg || searched?.CIN || "",
            jbkjs: currentCustomer?.jbkjs || searched?.jbkjs || "",
            otherIndificationNumber: currentCustomer?.otherIndificationNumber || "",
            email: currentCustomer?.email || "",
            phoneNumber: currentCustomer?.phoneNumber || currentCustomer?.telephone || "",
            address: currentCustomer?.address || searched?.address || "",
            city: currentCustomer?.city || searched?.city || "",
            country: currentCustomer?.country || "",
            contactPerson: currentCustomer?.contactPerson || "",
            isVerified: true
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentCustomer, searched]
    );

    const methods = useForm({
        resolver: yupResolver(NewUserSchema),
        defaultValues
    });

    const {
        reset,
        resetField,
        setValue,
        handleSubmit,
        control,
        formState: {errors, isSubmitting}
    } = methods;

    useEffect(() => {
        if (currentCustomer) {
            reset(defaultValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCustomer]);

    const taxCorePrefix = useWatch({control, name: "taxCorePrefix"});

    const selectedType = CUSTOMER_TYPES.find(type => type.value === parseInt(taxCorePrefix, 10));
    const needsOtherIdentification = isCustomerType(OTHER_IDENTIFICATIONS, taxCorePrefix);
    const disableTin = isCustomerType(DISABLE_TIN, taxCorePrefix);

    // Duplikat se prepoznaje po broju kojim se taj tip kupca identifikuje.
    const isDuplicate = (props) => allCustomers.some(customer => {
        if (isCustomerType(ONLY_TIN, props.taxCorePrefix)) {
            return customer.tin && customer.tin === props.tin;
        }
        if (isCustomerType(OTHER_IDENTIFICATIONS, props.taxCorePrefix)) {
            return customer.otherIndificationNumber
                && customer.otherIndificationNumber === props.otherIndificationNumber;
        }
        return customer.jmbg && customer.jmbg === props.jmbg;
    });

    const onSubmit = async (props) => {
        if (searched) {
            await dispatch(addCustomer(props)).unwrap().then(_ => {
                handleSaveAndSubmit(props)
                reset()
            }).catch(e => {
                console.error(e, "onSubmit")
                enqueueSnackbar("Čuvanje kupca nije uspelo.", {variant: "error"});
            })
            return;
        }

        if (!currentCustomer && isDuplicate(props)) {
            return enqueueSnackbar("Kupac sa istim identifikacionim brojem već postoji.", {variant: "warning"});
        }

        // Pad upisa NE sme da odvede na listu - korisnik bi mislio da je sačuvano.
        await dispatch(currentCustomer ? updateCustomer({...props, uid: currentCustomer.uid})
            : addCustomer(props)).unwrap().then(_ => {
            navigate(PATH_DASHBOARD.customers.root);
        }).catch(e => {
            console.error(e, "onSubmit");
            enqueueSnackbar("Čuvanje kupca nije uspelo.", {variant: "error"});
        });
    }

    const resetCompanyFields = () => {
        resetField("name");
        resetField("jmbg");
        resetField("jbkjs");
        resetField("city");
        resetField("country");
        resetField("address");
    };

    /**
     * Popuna po PIB-u. Kraći unos se ne šalje i NE briše već unete podatke - brisanje jedne cifre
     * je ranije brisalo ručno unet naziv i adresu.
     */
    const handleChangeTin = async (e) => {
        const value = e.target.value;
        setValue("tin", value);

        if (loadingTin || value.length !== TIN_LENGTH) {
            return;
        }

        setLoadingTin(true);
        await dispatch(getClientDetails(value)).unwrap().then(response => {
            if (!response) {
                return resetCompanyFields();
            }
            const {CIN, ZIP, address, city, name, jbkjs} = response;
            setValue("name", name || "");
            setValue("jmbg", CIN || "");
            setValue("jbkjs", jbkjs || "");
            setValue("city", city || "");
            setValue("country", SERBIA);
            setValue("address", address ? `${address}${ZIP ? `, ${ZIP}` : ""}` : "");
        }).catch(_ => {
            resetCompanyFields();
        }).finally(() => {
            setLoadingTin(false);
        });
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{p: 3.85}}>
                        <RHFAutocomplete
                            sx={{mb: 2}}
                            name="taxCorePrefix"
                            options={CUSTOMER_TYPES}
                            isOptionEqualToValue={(option, value) => option?.value === value}
                            onChange={(_, newValue) => {
                                if (!isCustomerType(DISABLE_TIN, newValue?.value) && !searched) {
                                    setValue("tin", "");
                                }
                                if (newValue?.value === 10) {
                                    setValue("country", SERBIA);
                                }
                                if (newValue?.value >= 30) {
                                    setValue("country", "");
                                }
                                setValue("taxCorePrefix", newValue?.value || null, {shouldValidate: true});
                            }}
                            getOptionLabel={(option) => {
                                if (option?.description) {
                                    return option.description;
                                }
                                return CUSTOMER_TYPES.find(type => type.value === option)?.description || "";
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Tip kupca*"
                                    variant="outlined"
                                    error={!!errors?.taxCorePrefix}
                                    helperText={errors?.taxCorePrefix?.message || ""}
                                    fullWidth/>
                            )}
                        />
                        <Box
                            rowGap={3}
                            columnGap={2}
                            display="grid"
                            gridTemplateColumns={{
                                xs: "repeat(1, 1fr)",
                                sm: "repeat(2, 1fr)"
                            }}>
                            <RHFTextField
                                name="tin"
                                label={`PIB${isCustomerType(ONLY_TIN, taxCorePrefix) ? "*" : ""}`}
                                disabled={disableTin}
                                onChange={handleChangeTin}
                                InputProps={loadingTin ? {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <CircularProgress size={18}/>
                                        </InputAdornment>
                                    )
                                } : undefined}/>
                            <RHFTextField name="name" label="Naziv*"/>
                            <RHFTextField
                                name="jmbg"
                                label={`Matični broj${isCustomerType(REQUIRED_JMBG, taxCorePrefix) ? "*" : ""}`}/>
                            <RHFTextField
                                name="jbkjs"
                                label={`JBKJS${isCustomerType(REQUIRED_JBKJS, taxCorePrefix) ? "*" : ""}`}/>
                            {needsOtherIdentification &&
                                <RHFTextField
                                    name="otherIndificationNumber"
                                    label={selectedType?.message || "Identifikacioni broj*"}/>}
                            <RHFTextField name="email" label="Mejl adresa"/>
                            <RHFTextField name="phoneNumber" label="Kontakt telefon"/>
                            <RHFSelect name="country" label="Zemlja" placeholder="Zemlja">
                                <option value=""/>
                                {countries.map((option) => (
                                    <option key={option.code} value={option.label}>
                                        {option.label}
                                    </option>
                                ))}
                            </RHFSelect>
                            <RHFTextField name="city" label="Grad"/>
                            <RHFTextField name="address" label="Adresa"/>
                            <RHFTextField name="contactPerson" label="Kontakt osoba"/>
                        </Box>
                    </Card>
                </Grid>
            </Grid>
            <Stack alignItems="flex-end" sx={{mt: 3}}>
                <LoadingButton
                    type={handleSaveAndSubmit ? "button" : "submit"}
                    onClick={handleSaveAndSubmit ? handleSubmit(onSubmit) : null}
                    variant="contained" loading={isSubmitting}>
                    Sačuvaj
                </LoadingButton>
            </Stack>
        </FormProvider>
    );
}
